/**
 * Cross-tenant group registry — list all managed groups, batch-add via link,
 * leave individual groups. Per-account group views live in the account
 * detail drawer.
 *
 * "Add groups" form accepts multiline input — each non-empty line is one
 * link/@username, which mirrors what mtcute.joinChat() takes. Failures are
 * shown alongside successes (partial join is normal under FLOOD_WAIT etc.).
 */

import { useMemo, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type AccountSummary,
  type AddGroupsResponse,
  type GroupStatsResponse,
  type GroupSummary,
  Routes,
} from '@tg-leads/contracts';

import { api } from '../../lib/api';
import { friendlyError } from '../../lib/friendlyError';

interface GroupList {
  items: GroupSummary[];
  nextCursor: string | null;
}
interface AccountList {
  items: AccountSummary[];
  nextCursor: string | null;
}

const statusBadge = (s: GroupSummary['status']) => {
  switch (s) {
    case 'active':
      return 'badge-ok';
    case 'readonly':
    case 'pending':
      return 'badge-warn';
    case 'kicked':
      return 'badge-fail';
    default:
      return 'badge-neutral';
  }
};

const statusLabel = (s: GroupSummary['status']): string => {
  switch (s) {
    case 'active':
      return '已加入';
    case 'pending':
      return '加群中';
    case 'readonly':
      return '只读';
    case 'left':
      return '已退出';
    case 'kicked':
      return '被踢出';
    default:
      return s;
  }
};

const STATUS_FILTER_LABEL: Record<'all' | GroupSummary['status'], string> = {
  all: '全部',
  active: '已加入',
  pending: '加群中',
  readonly: '只读',
  left: '已退出',
  kicked: '被踢出',
};

export function GroupsPage() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState<'all' | GroupSummary['status']>(
    'all',
  );

  const groups = useQuery({
    queryKey: ['groups'],
    queryFn: () => api.get<GroupList>(Routes.admin.groups),
  });

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get<AccountList>(Routes.admin.accounts),
  });

  const stats = useQuery({
    queryKey: ['groupStats'],
    queryFn: () => api.get<GroupStatsResponse>(Routes.admin.groupStats),
    refetchInterval: 30_000,
  });
  const statsByGroup = useMemo(() => {
    const m = new Map<string, GroupStatsResponse['items'][number]>();
    (stats.data?.items ?? []).forEach((s) => m.set(s.groupId, s));
    return m;
  }, [stats.data]);

  const renameMut = useMutation({
    mutationFn: (vars: { id: string; title: string }) =>
      api.patch<GroupSummary>(Routes.admin.group(vars.id), { title: vars.title }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });
  const rateMut = useMutation({
    mutationFn: (vars: { id: string; judgeSampleRate: number }) =>
      api.patch<GroupSummary>(Routes.admin.group(vars.id), { judgeSampleRate: vars.judgeSampleRate }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });

  const filtered = useMemo(() => {
    const items = groups.data?.items ?? [];
    if (statusFilter === 'all') return items;
    return items.filter((g) => g.status === statusFilter);
  }, [groups.data, statusFilter]);

  const leaveMut = useMutation({
    mutationFn: (groupId: string) =>
      api.post<GroupSummary>(Routes.admin.groupLeave(groupId), {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });

  const deleteMut = useMutation({
    mutationFn: (groupId: string) => api.delete(Routes.admin.group(groupId)),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['groups'] }),
  });

  const accountLookup = useMemo(() => {
    const map = new Map<string, AccountSummary>();
    (accounts.data?.items ?? []).forEach((a) => map.set(a.id, a));
    return map;
  }, [accounts.data]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold">已加入的群</h1>
          <p className="text-sm text-slate-500 mt-1">
            管理已加入的 TG 群。同一个群只在库里存一条记录，"加群账号"列显示这个群是哪个账号加的。
          </p>
        </div>
        <button className="btn-primary" onClick={() => setAddOpen(true)}>
          + 加入群
        </button>
      </div>

      <div className="card overflow-x-auto">
        <div className="flex items-center gap-3 mb-4 text-sm">
          <span className="text-slate-500">状态：</span>
          {(['all', 'active', 'pending', 'readonly', 'left', 'kicked'] as const).map(
            (s) => (
              <button
                key={s}
                className={`px-2 py-1 rounded text-xs ${
                  statusFilter === s
                    ? 'bg-indigo-600 text-white'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
                onClick={() => setStatusFilter(s)}
              >
                {STATUS_FILTER_LABEL[s]}
              </button>
            ),
          )}
        </div>

        {groups.isLoading ? (
          <div className="text-slate-500 text-sm">加载中…</div>
        ) : groups.error ? (
          <div className="text-red-600 text-sm">
            {(groups.error as Error).message}
          </div>
        ) : !filtered.length ? (
          <div className="text-slate-500 text-sm">该筛选下没有群。点右上角"加入群"添加。</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-slate-500 text-xs uppercase border-b">
                <th className="py-2 pr-4">群名</th>
                <th className="py-2 pr-4">TG 群 ID</th>
                <th className="py-2 pr-4">状态</th>
                <th className="py-2 pr-4 text-right">近 24h 消息</th>
                <th className="py-2 pr-4 text-right">累计消息</th>
                <th className="py-2 pr-4 text-right">已识别线索</th>
                <th className="py-2 pr-4">判定抽样</th>
                <th className="py-2 pr-4">加群账号</th>
                <th className="py-2 pr-4">操作</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((g) => {
                const joiner = g.joinedByAccountId
                  ? accountLookup.get(g.joinedByAccountId)
                  : null;
                const s = statsByGroup.get(g.id);
                return (
                  <tr key={g.id} className="border-b last:border-0">
                    <td className="py-3 pr-4 max-w-[18rem]">
                      <EditableTitle
                        title={g.title}
                        onSave={(title) =>
                          renameMut.mutate({ id: g.id, title })
                        }
                      />
                    </td>
                    <td className="py-3 pr-4 text-xs font-mono">{g.tgChatId}</td>
                    <td className="py-3 pr-4">
                      <span className={statusBadge(g.status)}>{statusLabel(g.status)}</span>
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums text-xs">
                      {s?.msgCount24h ?? 0}
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums text-xs text-slate-500">
                      {s?.msgCountTotal ?? 0}
                    </td>
                    <td className="py-3 pr-4 text-right tabular-nums text-xs">
                      <span
                        className={
                          (s?.leadCountTotal ?? 0) > 0
                            ? 'text-emerald-600 font-medium'
                            : 'text-slate-400'
                        }
                      >
                        {s?.leadCountTotal ?? 0}
                      </span>
                    </td>
                    <td className="py-3 pr-4 text-xs">
                      <select
                        className="input w-20 text-xs"
                        title="这个群有多大比例的消息送 LLM 判定。资源发现不受影响。扩散计划会自动设：新群 25%，媒介群 10%，出过线索的群 100%"
                        value={String(g.judgeSampleRate ?? 1)}
                        onChange={(e) => rateMut.mutate({ id: g.id, judgeSampleRate: Number(e.target.value) })}
                      >
                        {[1, 0.5, 0.25, 0.1, 0].map((v) => (
                          <option key={v} value={String(v)}>{v === 1 ? '全部' : v === 0 ? '不判' : `${Math.round(v * 100)}%`}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-3 pr-4 text-xs">
                      {joiner
                        ? joiner.tgUsername
                          ? `@${joiner.tgUsername}`
                          : joiner.phone
                        : g.joinedByAccountId?.slice(0, 8) ?? '—'}
                    </td>
                    <td className="py-3 pr-4 space-x-2">
                      <button
                        className="btn-secondary text-xs"
                        onClick={() => {
                          if (confirm(`退出 "${g.title}"?`)) leaveMut.mutate(g.id);
                        }}
                        disabled={
                          leaveMut.isPending ||
                          g.status === 'left' ||
                          g.status === 'kicked'
                        }
                      >
                        退群
                      </button>
                      <button
                        className="btn-danger text-xs"
                        onClick={() => {
                          if (
                            confirm(
                              `从本地数据库删除 "${g.title}"?（不会让账号退群，只是删本地记录）`,
                            )
                          )
                            deleteMut.mutate(g.id);
                        }}
                        disabled={deleteMut.isPending}
                      >
                        删除记录
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {leaveMut.error && (
          <div className="text-xs text-red-600 mt-3">
            {(leaveMut.error as Error).message}
          </div>
        )}
      </div>

      {addOpen && (
        <AddGroupsDialog
          accounts={accounts.data?.items ?? []}
          onDone={() => {
            qc.invalidateQueries({ queryKey: ['groups'] });
            setAddOpen(false);
          }}
          onClose={() => setAddOpen(false)}
        />
      )}
    </div>
  );
}

function AddGroupsDialog({
  accounts,
  onClose,
  onDone,
}: {
  accounts: AccountSummary[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [accountId, setAccountId] = useState('');
  const [linksText, setLinksText] = useState('');
  const [result, setResult] = useState<AddGroupsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const eligible = accounts.filter(
    (a) => a.status === 'active' && a.warmupPhase === 'ready',
  );

  const mut = useMutation({
    mutationFn: () => {
      const links = linksText
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
      return api.post<AddGroupsResponse>(Routes.admin.groups, {
        joinByAccountId: accountId,
        links,
      });
    },
    onSuccess: (data) => setResult(data),
    onError: (e: unknown) =>
      setErr(friendlyError(e)),
  });

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="card w-[560px] space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-lg font-semibold">加入群</h2>

        {result ? (
          <div className="space-y-3">
            <div className="text-sm text-green-700 bg-green-50 p-3 rounded">
              ✓ 已加入 {result.created.length} 个
              {result.pendingApprovals.length > 0 && (
                <> · 等管理员审批 {result.pendingApprovals.length} 个</>
              )}
              {result.failures.length > 0 && (
                <> · 失败 {result.failures.length} 个</>
              )}
            </div>
            {result.pendingApprovals.length > 0 && (
              <div className="text-xs text-amber-800 bg-amber-50 p-3 rounded space-y-1">
                <div className="font-semibold">⏳ 等群主审批：</div>
                <ul className="space-y-1">
                  {result.pendingApprovals.map((p, i) => (
                    <li key={i}>
                      <span className="font-mono">{p.link}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[10px] text-amber-700 mt-1">
                  群主通过后，下一条群内消息会自动让这个群出现在列表里。
                </p>
              </div>
            )}
            {result.failures.length > 0 && (
              <ul className="text-xs text-red-700 bg-red-50 p-3 rounded space-y-1">
                {result.failures.map((f, i) => (
                  <li key={i}>
                    <span className="font-mono">{f.link}</span>: {f.reason}
                  </li>
                ))}
              </ul>
            )}
            <div className="flex justify-end">
              <button className="btn-primary" onClick={onDone}>完成</button>
            </div>
          </div>
        ) : (
          <form
            onSubmit={(e: FormEvent) => {
              e.preventDefault();
              setErr(null);
              mut.mutate();
            }}
            className="space-y-4"
          >
            <div>
              <label className="label">使用哪个账号加群</label>
              {!eligible.length ? (
                <div className="text-sm text-yellow-700 bg-yellow-50 px-3 py-2 rounded">
                  没有可用账号（需要状态"启用中"且养号阶段"就绪"）。
                </div>
              ) : (
                <select
                  className="input"
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  required
                >
                  <option value="">— 选择账号 —</option>
                  {eligible.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.tgUsername ? `@${a.tgUsername} (${a.phone})` : a.phone}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <label className="label">群链接或 @用户名（每行一个）</label>
              <textarea
                className="input font-mono text-xs"
                rows={6}
                value={linksText}
                onChange={(e) => setLinksText(e.target.value)}
                placeholder={'https://t.me/+abc\n@somegroup'}
                required
              />
              <p className="text-xs text-slate-400 mt-1">
                单次最多 100 条。TG 加群有频率限制，建议分批添加更稳。
              </p>
            </div>
            {err && (
              <div className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded">
                {err}
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <button type="button" className="btn-secondary" onClick={onClose}>
                取消
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={mut.isPending || !accountId || !linksText.trim()}
              >
                {mut.isPending ? '加群中…' : '开始加群'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

/**
 * Click-to-edit title. Renders truncated text by default; click toggles
 * an input with save/cancel. Used in the Groups table to rename
 * auto-discovered placeholder titles to something human-readable.
 */
function EditableTitle({
  title,
  onSave,
}: {
  title: string;
  onSave: (next: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(title);
  if (!editing) {
    return (
      <div
        className="truncate cursor-text hover:bg-slate-50 px-1 -mx-1 rounded"
        title={`${title}  （点击编辑）`}
        onClick={() => {
          setDraft(title);
          setEditing(true);
        }}
      >
        {title}
      </div>
    );
  }
  const commit = () => {
    const v = draft.trim();
    if (v && v !== title) onSave(v);
    setEditing(false);
  };
  return (
    <input
      autoFocus
      className="border border-indigo-400 rounded px-2 py-1 w-full text-sm"
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') commit();
        if (e.key === 'Escape') {
          setDraft(title);
          setEditing(false);
        }
      }}
    />
  );
}
